import { UPGRADES } from '../config/tuning';
import { PICKUP, VEHICLES, vehicleById, type VehicleDef, type VehicleId } from '../config/vehicles';

export const UPGRADE_IDS = ['engine', 'tyres', 'suspension', 'tank'] as const;
export type UpgradeId = (typeof UPGRADE_IDS)[number];
export type UpgradeLevels = Record<UpgradeId, number>;

export const NO_UPGRADES: Readonly<UpgradeLevels> = Object.freeze({ engine: 0, tyres: 0, suspension: 0, tank: 0 });

/** What a run reads from the garage: the vehicle plus its upgraded tunables. Level 0 = the vehicle's base values exactly. */
export interface VehicleStats {
  vehicle: VehicleDef;
  acceleration: number;
  topSpeed: number;
  frictionSlip: number;
  suspensionTravel: number;
  suspensionCompression: number;
  suspensionRelaxation: number;
  fuelCapacity: number;
  drainIdle: number;
  drainThrottle: number;
}

export function vehicleStats(levels: Readonly<UpgradeLevels> = NO_UPGRADES, vehicle: VehicleDef = PICKUP): VehicleStats {
  const u = UPGRADES;
  const damping = 1 + u.suspension.dampingPerLevel * levels.suspension;
  return {
    vehicle,
    acceleration: vehicle.drive.acceleration * (1 + u.engine.accelerationPerLevel * levels.engine),
    topSpeed: vehicle.drive.topSpeed * (1 + u.engine.topSpeedPerLevel * levels.engine),
    frictionSlip: vehicle.wheel.frictionSlip * (1 + u.tyres.gripPerLevel * levels.tyres),
    suspensionTravel: vehicle.suspension.travel + u.suspension.travelPerLevel * levels.suspension,
    suspensionCompression: vehicle.suspension.compression * damping,
    suspensionRelaxation: vehicle.suspension.relaxation * damping,
    // The tank upgrade adds the same share of each vehicle's base tank (+20 % per level).
    fuelCapacity: vehicle.fuel.capacity * (1 + (u.tank.capacityPerLevel / 100) * levels.tank),
    drainIdle: vehicle.fuel.drainIdle,
    drainThrottle: vehicle.fuel.drainThrottle,
  };
}

/** Saved garage: wallet, owned vehicles, the one being driven, and upgrade levels per vehicle. */
export interface Garage {
  coins: number;
  vehicle: VehicleId;
  owned: VehicleId[];
  upgrades: Record<VehicleId, UpgradeLevels>;
}

export function emptyGarage(coins = 0): Garage {
  return { coins, vehicle: 'pickup', owned: ['pickup'], upgrades: { pickup: { ...NO_UPGRADES }, motor: { ...NO_UPGRADES }, truck: { ...NO_UPGRADES } } };
}

/** Upgrade levels of the vehicle being driven. */
export function currentLevels(garage: Readonly<Garage>): UpgradeLevels {
  return garage.upgrades[garage.vehicle];
}

export function garageStats(garage: Readonly<Garage>): VehicleStats {
  return vehicleStats(currentLevels(garage), vehicleById(garage.vehicle) ?? PICKUP);
}

/** Price of the next level, or null at max. */
export function nextCost(levels: Readonly<UpgradeLevels>, id: UpgradeId): number | null {
  const level = levels[id];
  return level >= UPGRADES.maxLevel ? null : UPGRADES.costs[level];
}

/** Buys one level for the vehicle being driven, if affordable and not maxed. Pure. */
export function buyUpgrade(garage: Readonly<Garage>, id: UpgradeId): Garage | null {
  const levels = currentLevels(garage);
  const cost = nextCost(levels, id);
  if (cost === null || garage.coins < cost) return null;
  return {
    ...garage,
    coins: garage.coins - cost,
    upgrades: { ...garage.upgrades, [garage.vehicle]: { ...levels, [id]: levels[id] + 1 } },
  };
}

/** Buys a vehicle and switches to it. Pure. */
export function buyVehicle(garage: Readonly<Garage>, id: VehicleId): Garage | null {
  const v = vehicleById(id);
  if (!v || garage.owned.includes(id) || garage.coins < v.price) return null;
  return { ...garage, coins: garage.coins - v.price, owned: [...garage.owned, id], vehicle: id };
}

export function selectVehicle(garage: Readonly<Garage>, id: VehicleId): Garage | null {
  return garage.owned.includes(id) ? { ...garage, vehicle: id } : null;
}

function sanitizeLevels(raw: unknown): UpgradeLevels {
  const data = (raw ?? {}) as Record<string, unknown>;
  const levels = { ...NO_UPGRADES };
  for (const id of UPGRADE_IDS) {
    const v = data[id];
    if (typeof v === 'number' && Number.isFinite(v)) levels[id] = Math.max(0, Math.min(UPGRADES.maxLevel, Math.floor(v)));
  }
  return levels;
}

/** Coerces untrusted saved data into a valid garage. Phase 3 saves ({ coins, levels }) migrate onto the pickup. */
export function sanitizeGarage(raw: unknown): Garage {
  const data = (raw ?? {}) as { coins?: unknown; levels?: unknown; vehicle?: unknown; owned?: unknown; upgrades?: Record<string, unknown> };
  const coins = typeof data.coins === 'number' && Number.isFinite(data.coins) ? Math.max(0, Math.floor(data.coins)) : 0;
  const garage = emptyGarage(coins);
  if (data.levels) garage.upgrades.pickup = sanitizeLevels(data.levels);
  for (const v of VEHICLES) if (data.upgrades?.[v.id]) garage.upgrades[v.id] = sanitizeLevels(data.upgrades[v.id]);
  if (Array.isArray(data.owned)) {
    for (const id of data.owned) if (typeof id === 'string' && vehicleById(id) && !garage.owned.includes(id as VehicleId)) garage.owned.push(id as VehicleId);
  }
  if (typeof data.vehicle === 'string' && garage.owned.includes(data.vehicle as VehicleId)) garage.vehicle = data.vehicle as VehicleId;
  return garage;
}
